import React, { useState, useRef, useEffect, useMemo } from "react";
import Label from "./Label";

const CustomDropdown = ({
    data = [],
    labelKey = 'title',
    valueKey = 'id',
    searchFilter = true,
    buttonClass,
    dropDownClass,
    limit = 10,
    defaultValue = null,
    seletctText = 'Select',
    label = '',
    handleChange,
    disabled = false,
    tooltipData = null,
    serverSideSearchData = false,
    setSearchTerm,
    keyName,
    required,
}) => {
    // Styles for dropdown menu
    const styles = {
        customDropdownMenu: {
            zIndex: 10,
            position: 'relative',
            transition: "all 0.3s ease",
            transformOrigin: "top center",
            transform: "scaleY(0)",
            opacity: 0,
            visibility: "hidden",
            top: 0,
            left: 0,
            right: 0,
            overflow: "hidden",
        },
        customDropdownMenuOpen: {
            transform: "scaleY(1)",
            opacity: 1,
            visibility: "visible",
        },
        customDropdownItem: {
            transition: "background-color 0.3s ease",
        },
    };

    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const wrapperRef = useRef(null);
    const [endIndex, setEndIndex] = useState(limit);

    const filteredItems = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return [];
        }
        return search
            ? data.filter(item => item[labelKey].toLowerCase().includes(search.toLowerCase()))
            : data;
    }, [data, search, labelKey]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearch("");
                setEndIndex(limit);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [limit]);

    useEffect(() => {
        if (defaultValue && (!selectedValue || selectedValue[valueKey] !== defaultValue[valueKey])) {
            setSelectedValue(defaultValue);
        }
    }, [defaultValue, selectedValue, valueKey]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        if (setSearchTerm) {
            setSearchTerm(e.target.value);
        }
    };

    const handleSelect = (item) => {
        setSelectedValue(item);
        setIsOpen(false);
        if (handleChange) {
            handleChange({ keyName, label, ...item });
        }
    };

    const handleScroll = (event) => {
        const element = event.target;
        const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 5;
        if (atBottom && endIndex < data.length) {
            setEndIndex(prevEndIndex => prevEndIndex + limit);
        }
    };

    return (
        <div ref={wrapperRef} className="sd-custom-dropdown sd-relative sd-w-full sd-h-20">
            <Label tooltipData={tooltipData} disabled={disabled} label={label} required={required} />
            <button
                type="button"
                disabled={disabled}
                className={`${buttonClass} sd-relative sd-block sd-w-full sd-p-2 sd-bg-gray-200 sd-border ${disabled ? "sd-border-gray-100 sd-cursor-not-allowed" : "sd-border-gray-300"} sd-outline-none sd-custom-dropdown-button sd-text-black sd-custom-form`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="sd-flex sd-items-center sd-justify-between">
                    <span>{selectedValue && selectedValue[labelKey] ? selectedValue[labelKey] : seletctText}</span>
                    <input type="hidden" data-key={keyName} data-label={label} required={required} name={labelKey} value={selectedValue && selectedValue[labelKey] ? selectedValue[labelKey] : ''} />
                    <input type="hidden" name={valueKey} value={selectedValue && selectedValue[valueKey] ? selectedValue[valueKey] : ''} />
                    <div className="sd-flex sd-items-center">
                        <svg className={`custom-dropdown-arrow ${isOpen ? 'sd-open' : ''}`} stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                            <path fill="none" d="M0 0h24v24H0V0z"></path>
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"></path>
                        </svg>
                    </div>
                </div>
            </button>
            <div style={{ ...styles.customDropdownMenu, ...(isOpen ? styles.customDropdownMenuOpen : {}) }} className={`${dropDownClass} sd-mb-4 sd-block sd-w-full sd-p-2 sd-mt-1 sd-bg-white sd-border sd-border-gray-300 sd-shadow-md sd-custom-dropdown-menu ${isOpen ? 'sd-open' : ''}`}>
                {searchFilter && (
                    <input
                        type="text"
                        value={search}
                        onChange={handleSearch}
                        className={`sd-block sd-w-full sd-mb-4 sd-shadow-sm sd-p-2 sd-border sd-border-gray-300 sd-custom-dropdown-search ${disabled ? "sd-bg-gray-100 sd-cursor-not-allowed" : ""}`}
                        placeholder="Search..."
                        disabled={disabled}
                    />
                )}
                <ul className="sd-max-h-48 sd-overflow-y-auto sd-text-left" onScroll={handleScroll}>
                    {filteredItems.slice(0, endIndex).map((item, index) => (
                        <li
                            key={index}
                            style={styles.customDropdownItem}
                            className={`sd-p-2 hover:sd-bg-gray-200 sd-cursor-pointer sd-custom-dropdown-item ${disabled ? "sd-cursor-not-allowed" : ""}`}
                            onClick={() => !disabled && handleSelect(item)}
                        >
                            {item[labelKey] ? item[labelKey] : item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default CustomDropdown;
